import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  InsertDriveFileOutlined as DocumentIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { downloadAuditPlanFile } from "../../utils/auditPlanFileUtils";
import { renderValidationStatusChip } from "../../../Frameworks/FrameWorkDialogs/Control-Components/ControlComponents";
import { EvidenceStatus } from "../../../../../utils/enums";

const getValidationStatus = (file) => {
  if (file?.isFileValid === true) {
    return { status: EvidenceStatus.VALID, message: "Acceptable" };
  }
  if (file?.isFileValid === false) {
    return { status: EvidenceStatus.INVALID, message: "Invalid" };
  }
  return { status: EvidenceStatus.PROCESSING, message: "Processing..." };
};

const VIEWER_HEIGHT = "min(800px, calc(100vh - 280px))";

const formatUploadedAt = (value) => {
  if (!value) return "";
  const d = new Date(Number.isFinite(+value) ? +value : value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AuditPlanFileList = ({
  files,
  selectedFileUuid,
  onSelect,
  onDelete,
  isAuditorView = false,
  busy = false,
}) => {
  const theme = useTheme();
  const borderColor = theme.customColors?.border?.main ?? "#E5E7EB";
  const hoverBg = theme.customColors?.background?.hover ?? "#F5F6F8";
  const selectedBg = "rgba(3, 27, 137, 0.06)";
  const accent = "#031B89";

  return (
    <Box
      sx={{
        width: "100%",
        height: VIEWER_HEIGHT,
        maxHeight: VIEWER_HEIGHT,
        minHeight: "500px",
        borderRadius: "8px",
        border: `1px solid ${borderColor}`,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor: "#FAFBFC",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
          Files ({files.length})
        </Typography>
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {files.map((file) => {
          const isSelected = selectedFileUuid === file.fileUuid;
          return (
            <Box
              key={file.fileUuid || file.fileId}
              onClick={() => onSelect?.(file)}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 2,
                py: 1.75,
                cursor: "pointer",
                borderBottom: `1px solid ${borderColor}`,
                backgroundColor: isSelected ? selectedBg : "transparent",
                transition: "background-color 0.15s",
                "&::before": isSelected
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      backgroundColor: accent,
                    }
                  : {},
                "&:hover": {
                  backgroundColor: isSelected ? selectedBg : hoverBg,
                },
                "&:hover .file-row-actions": {
                  opacity: 1,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  backgroundColor: "rgba(3, 27, 137, 0.08)",
                  color: accent,
                  flexShrink: 0,
                }}
              >
                <DocumentIcon sx={{ fontSize: 18 }} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={file.maskName || ""} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: 13,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: isSelected ? accent : "text.primary",
                    }}
                  >
                    {file.maskName || "Untitled file"}
                  </Typography>
                </Tooltip>
                {file.createdAt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 11, mt: 0.25, display: "block" }}
                  >
                    {formatUploadedAt(file.createdAt)}
                  </Typography>
                )}
                <Box sx={{ mt: 0.75 }}>
                  <Tooltip
                    title={
                      file?.isFileValid === false && file?.fileRejectReason
                        ? file.fileRejectReason
                        : ""
                    }
                    arrow
                    disableHoverListener={
                      !(file?.isFileValid === false && file?.fileRejectReason)
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        "& .MuiChip-root": { height: 24 },
                        "& .MuiChip-label": {
                          px: 1.25,
                          fontSize: "0.7rem",
                        },
                        "& .MuiChip-icon": { fontSize: 17 },
                      }}
                    >
                      {renderValidationStatusChip(getValidationStatus(file))}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              <Box
                className="file-row-actions"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  flexShrink: 0,
                  opacity: isSelected ? 1 : 0.55,
                  transition: "opacity 0.15s",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Download">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => downloadAuditPlanFile(file)}
                      disabled={!file.path || busy}
                      sx={{ color: "text.secondary" }}
                    >
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                {!isAuditorView && onDelete && (
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(file)}
                        disabled={busy}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "#C71414" },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AuditPlanFileList;
